/**
 * Client-side lead field validation that mirrors the bot's business rules
 * (src/services/stateMachine.ts + intentRouter.ts) so a manually-entered lead is
 * held to the same standards the WhatsApp funnel enforces:
 *
 *  - Youngest driver age must be within the insurable band 17.5–85.
 *  - License seniority (ותק) is 0–80 years AND consistent with the age: a driver
 *    can't have held a license before age 16.75 (age − years ≥ 16.75).
 *  - Insurance start date must be between today and the 15th of next month; a
 *    later date is only an *estimate* (rates change), a past date is invalid.
 *  - Car number is 7–8 digits; national ID is 5–12 digits.
 *
 * Each check returns a {level, text} issue (or null). `error` marks a value the
 * bot would reject; `warn` marks something that still prices but only as an
 * estimate / non-standard input.
 */

export const INSURABLE_MIN_AGE = 17.5;
export const INSURABLE_MAX_AGE = 85;
export const MIN_LICENSE_AGE = 16.75;
export const LICENSE_MAX_YEARS = 80;

export type IssueLevel = 'error' | 'warn';
export interface FieldIssue {
  level: IssueLevel;
  text: string;
}

export interface LeadLike {
  youngest_driver_age?: number;
  youngest_driver_birth_date?: string;
  youngest_driver_license_years?: number;
  insurance_start_date?: string;
  car_number?: string;
  national_id?: string;
}

/** The furthest issuable start date: the 15th of next month (matches the bot). */
export function maxStartDate(now: Date = new Date()): Date {
  return new Date(now.getFullYear(), now.getMonth() + 1, 15);
}

/** Whole-year age from an ISO birth date, or undefined when absent/invalid. */
export function ageFromBirthDate(iso?: string): number | undefined {
  if (!iso) return undefined;
  const b = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(b.getTime())) return undefined;
  const now = new Date();
  let age = now.getFullYear() - b.getFullYear();
  const m = now.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < b.getDate())) age -= 1;
  return age;
}

/** The age the pricing uses: explicit age wins, else derived from birth date. */
export function effectiveAge(l: LeadLike): number | undefined {
  if (typeof l.youngest_driver_age === 'number' && !Number.isNaN(l.youngest_driver_age)) {
    return l.youngest_driver_age;
  }
  return ageFromBirthDate(l.youngest_driver_birth_date);
}

const fmt = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(1));

export function ageIssue(l: LeadLike): FieldIssue | null {
  const age = l.youngest_driver_age;
  if (age === undefined || age === null || Number.isNaN(age)) return null;
  if (age < INSURABLE_MIN_AGE) {
    return { level: 'error', text: `גיל הנהג הצעיר (${fmt(age)}) מתחת למינימום לביטוח (17.5) — לא ניתן לבטח.` };
  }
  if (age > INSURABLE_MAX_AGE) {
    return { level: 'error', text: `גיל הנהג הצעיר (${fmt(age)}) מעל המקסימום לביטוח (85) — לא ניתן לבטח.` };
  }
  return null;
}

export function birthDateIssue(l: LeadLike): FieldIssue | null {
  if (!l.youngest_driver_birth_date) return null;
  const age = ageFromBirthDate(l.youngest_driver_birth_date);
  if (age === undefined) return { level: 'error', text: 'תאריך לידה לא תקין.' };
  if (age < 0) return { level: 'error', text: 'תאריך לידה עתידי.' };
  if (age < INSURABLE_MIN_AGE) return { level: 'error', text: `לפי תאריך הלידה הגיל הוא ${age} — מתחת למינימום לביטוח (17.5).` };
  if (age > INSURABLE_MAX_AGE) return { level: 'error', text: `לפי תאריך הלידה הגיל הוא ${age} — מעל המקסימום לביטוח (85).` };
  return null;
}

export function licenseYearsIssue(l: LeadLike): FieldIssue | null {
  const years = l.youngest_driver_license_years;
  if (years === undefined || years === null || Number.isNaN(years)) return null;
  if (years < 0 || years > LICENSE_MAX_YEARS) {
    return { level: 'error', text: `ותק רישיון חייב להיות בין 0 ל-${LICENSE_MAX_YEARS} שנים.` };
  }
  const age = effectiveAge(l);
  if (typeof age === 'number' && age - years < MIN_LICENSE_AGE) {
    const suggested = Math.max(0, Math.floor(age - MIN_LICENSE_AGE));
    return {
      level: 'error',
      text: `ותק ${fmt(years)} לא תואם לגיל ${fmt(age)} — משמעו רישיון לפני גיל 16.9. הוותק המקסימלי ההגיוני: כ-${suggested} שנים.`,
    };
  }
  return null;
}

export function startDateIssue(l: LeadLike, now: Date = new Date()): FieldIssue | null {
  const iso = l.insurance_start_date;
  if (!iso) return null;
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return { level: 'error', text: 'תאריך תחילת ביטוח לא תקין.' };
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const max = maxStartDate(now);
  if (d < today) return { level: 'error', text: 'תאריך תחילת הביטוח כבר עבר — בחר תאריך עתידי.' };
  if (d > max) {
    return {
      level: 'warn',
      text: `התאריך מעבר לחלון ההנפקה (עד ${max.toLocaleDateString('he-IL')}) — התמחור יתקבל להערכה בלבד, המחירים עשויים להשתנות קרוב למועד.`,
    };
  }
  return null;
}

export function carNumberIssue(l: LeadLike): FieldIssue | null {
  if (!l.car_number) return null;
  const digits = l.car_number.replace(/\D/g, '');
  if (digits.length < 7 || digits.length > 8) {
    return { level: 'warn', text: 'מספר רכב תקין הוא 7–8 ספרות.' };
  }
  return null;
}

export function nationalIdIssue(l: LeadLike): FieldIssue | null {
  if (!l.national_id) return null;
  const digits = l.national_id.replace(/\D/g, '');
  if (digits.length < 5 || digits.length > 12) {
    return { level: 'warn', text: 'ת"ז צריכה להכיל 5–12 ספרות.' };
  }
  return null;
}

/** All active issues, keyed by the Lead field name (for inline rendering). */
export function leadFieldIssues(l: LeadLike): Record<string, FieldIssue> {
  const out: Record<string, FieldIssue> = {};
  const add = (field: string, issue: FieldIssue | null) => {
    if (issue) out[field] = issue;
  };
  add('youngest_driver_age', ageIssue(l));
  add('youngest_driver_birth_date', birthDateIssue(l));
  add('youngest_driver_license_years', licenseYearsIssue(l));
  add('insurance_start_date', startDateIssue(l));
  add('car_number', carNumberIssue(l));
  add('national_id', nationalIdIssue(l));
  return out;
}

/** True when any issue is a hard error (a value the bot would reject). */
export function hasBlockingError(issues: Record<string, FieldIssue>): boolean {
  return Object.values(issues).some((i) => i.level === 'error');
}
